<template>
  <b-modal
    id="modal-leave-detail"
    ref="modal-leave-detail"
    title="Leave Request Details"
    ok-only
    ok-title=""
    cancel-title=""
    modal-class="leave-detail-modal"
    header-class="leave-detail-modal-header"
    @hidden="$emit('hidden')"
    size="md"
    :footer-class="leaveDetailFooterClass"
    body-class="leave-detail-modal-body"
  >
    <div v-if="selectedLeaveForDetail" class="leave-detail-content">
      <b-card no-body class="mb-0 leave-detail-main-card">
        <div class="leave-detail-main-card__body">
        <!-- Type + status -->
        <div class="leave-detail-header">
          <h5 class="mb-0 leave-detail-type">
            <feather-icon icon="CalendarIcon" size="16" class="leave-detail-type-icon" />
            <span>{{ formatLeaveType(selectedLeaveForDetail.leave_type) }}</span>
          </h5>
          <b-badge
            :variant="getApprovalStatusVariant(selectedLeaveForDetail.status)"
            class="mb-0 leave-detail-status leave-detail-status-pill"
          >
            {{ formatApprovalStatus(selectedLeaveForDetail.status) }}
          </b-badge>
        </div>

        <b-alert v-if="viewMode === 'team' && selectedEmployeeLeaveBalance" variant="info" show class="mb-2 py-1 px-2 leave-detail-balance-alert">
          <div class="d-flex align-items-center">
            <feather-icon icon="InfoIcon" size="14" class="mr-2" />
            <div>
              <strong>Employee Leave Balance:</strong>
              <span class="ml-1">{{ selectedEmployeeLeaveBalance }}</span>
            </div>
          </div>
        </b-alert>

        <!-- Detail rows: icon + label above value -->
        <div class="leave-detail-grid">
          <div class="leave-detail-item">
            <div class="leave-detail-label">
              <feather-icon icon="UserIcon" size="14" />Employee
            </div>
            <div class="leave-detail-value">{{ viewMode === 'team' ? getUserName(selectedLeaveForDetail.emp_id) : 'Me' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">
              <feather-icon icon="ClockIcon" size="14" />Duration
            </div>
            <div class="leave-detail-value">{{ durationLabel }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">
              <feather-icon icon="CalendarIcon" size="14" />From
            </div>
            <div class="leave-detail-value leave-detail-value--date">{{ formatDate(selectedLeaveForDetail.from_date) || '-' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">
              <feather-icon icon="CalendarIcon" size="14" />To
            </div>
            <div class="leave-detail-value leave-detail-value--date">{{ formatDate(selectedLeaveForDetail.to_date) || '-' }}</div>
          </div>
        </div>

        <!-- Comments & Remarks -->
        <div class="leave-detail-notes">
          <div class="leave-detail-section leave-detail-section--note">
            <div class="leave-detail-note-head">
              <feather-icon icon="MessageSquareIcon" size="14" />Comments
            </div>
            <p
              class="mb-0 leave-detail-note"
              :class="{ 'leave-detail-note--empty': !selectedLeaveForDetail.comment }"
            >
              {{ selectedLeaveForDetail.comment || 'No comments' }}
            </p>
          </div>

          <div class="leave-detail-section leave-detail-section--note">
            <div class="leave-detail-note-head">
              <feather-icon icon="UserCheckIcon" size="14" />Remarks
            </div>
            <p class="mb-0 leave-detail-note" :class="{ 'leave-detail-note--empty': !managerRemarks }">
              {{ managerRemarks || 'No remarks from manager' }}
            </p>
          </div>
        </div>
        </div>
      </b-card>

      <b-card
        v-if="selectedLeaveForDetail.leave_type === 'CO_REQUEST' || (selectedLeaveForDetail.comment && selectedLeaveForDetail.comment.includes('CO Balance Request'))"
        class="mb-0 mt-2 leave-detail-co-card"
      >
        <h6 class="mb-2 leave-detail-co-title">
          <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
          CO Balance Request Details
        </h6>
        <div class="leave-detail-grid leave-detail-grid--co">
          <div class="leave-detail-item">
            <div class="leave-detail-label">Employee ID</div>
            <div class="leave-detail-value">{{ selectedLeaveForDetail.emp_id || '-' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">Department</div>
            <div class="leave-detail-value">{{ getApplicantDepartment(selectedLeaveForDetail) || currentUserDepartment || '-' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">Request Date</div>
            <div class="leave-detail-value leave-detail-value--date">{{ formatDate(selectedLeaveForDetail.created_at || selectedLeaveForDetail.createdAt) || '-' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">CO Days Worked</div>
            <div class="leave-detail-value leave-detail-value--date">{{ formatDate(selectedLeaveForDetail.from_date || selectedLeaveForDetail.work_date) || '-' }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">CO Days Req.</div>
            <div class="leave-detail-value">{{ coDaysRequestedLabel }}</div>
          </div>
          <div class="leave-detail-item">
            <div class="leave-detail-label">CO Balance</div>
            <div class="leave-detail-value leave-detail-value--accent">{{ coBalanceLabel }}</div>
          </div>
          <div class="leave-detail-item leave-detail-item--full leave-detail-item--stack">
            <div class="leave-detail-note-head">Reason</div>
            <p class="mb-0 leave-detail-note">{{ parseCoRequestReason(selectedLeaveForDetail.comment) || '-' }}</p>
          </div>
        </div>
      </b-card>

      <!-- Team/Org approve/reject (unchanged); employee Cancel/Edit live in footer -->
      <div
        v-if="showTeamApprovalActions"
        class="leave-detail-approval"
      >
        <!-- Manager: approve/reject when pending manager approval -->
        <template v-if="isManagerOfEmployee(selectedLeaveForDetail.emp_id) && isLeavePendingManagerApproval(selectedLeaveForDetail) && canApproveLeaveManagerStage">
          <b-form-group label="Comment" label-for="leave-detail-comment" class="mb-2">
            <b-form-textarea
              id="leave-detail-comment"
              :value="leaveDetailComment"
              rows="2"
              placeholder="Please provide a comment (required)"
              required
              @input="$emit('update:leaveDetailComment', $event)"
            />
            <small class="text-danger">Comment is mandatory for approval/rejection</small>
          </b-form-group>
          <div class="text-center">
            <b-button variant="success" class="mr-2" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('approve', selectedLeaveForDetail)">
              <feather-icon icon="CheckIcon" size="14" class="mr-1" />Approve
            </b-button>
            <b-button variant="danger" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('reject', selectedLeaveForDetail)">
              <feather-icon icon="XIcon" size="14" class="mr-1" />Reject
            </b-button>
          </div>
        </template>

        <!-- HR: leave pending HR approval -->
        <template v-else-if="isLeavePendingHRApproval(selectedLeaveForDetail) && canApproveLeaveHRStage">
          <b-form-group label="Comment" label-for="leave-detail-comment-hr" class="mb-2">
            <b-form-textarea
              id="leave-detail-comment-hr"
              :value="leaveDetailComment"
              rows="2"
              placeholder="Please provide a comment (required)"
              required
              @input="$emit('update:leaveDetailComment', $event)"
            />
            <small class="text-danger">Comment is mandatory for approval/rejection</small>
          </b-form-group>
          <b-form-group v-if="isApplicantOnProbation(selectedLeaveForDetail)" label="Do you want to mark this as Paid Holiday?" class="mb-2">
            <b-form-radio-group
              :value="leaveDetailIsPaid"
              :options="[{ value: true, text: 'Yes' }, { value: false, text: 'No' }]"
              @input="$emit('update:leaveDetailIsPaid', $event)"
            />
          </b-form-group>
          <div class="text-center">
            <b-button variant="success" class="mr-2" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('approve', selectedLeaveForDetail)">
              <feather-icon icon="CheckIcon" size="14" class="mr-1" />Approve
            </b-button>
            <b-button variant="danger" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('reject', selectedLeaveForDetail)">
              <feather-icon icon="XIcon" size="14" class="mr-1" />Reject
            </b-button>
          </div>
        </template>

        <!-- leave_all (org view): can approve any pending leave -->
        <template v-else-if="canApproveLeaveAllStages">
          <b-form-group label="Comment" label-for="leave-detail-comment-all" class="mb-2">
            <b-form-textarea
              id="leave-detail-comment-all"
              :value="leaveDetailComment"
              rows="2"
              placeholder="Please provide a comment (required)"
              required
              @input="$emit('update:leaveDetailComment', $event)"
            />
            <small class="text-danger">Comment is mandatory for approval/rejection</small>
          </b-form-group>
          <b-form-group v-if="isApplicantOnProbation(selectedLeaveForDetail)" label="Do you want to mark this as Paid Holiday?" class="mb-2">
            <b-form-radio-group
              :value="leaveDetailIsPaid"
              :options="[{ value: true, text: 'Yes' }, { value: false, text: 'No' }]"
              @input="$emit('update:leaveDetailIsPaid', $event)"
            />
          </b-form-group>
          <div class="text-center">
            <b-button variant="success" class="mr-2" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('approve', selectedLeaveForDetail)">
              <feather-icon icon="CheckIcon" size="14" class="mr-1" />Approve
            </b-button>
            <b-button variant="danger" :disabled="!leaveDetailComment || !leaveDetailComment.trim()" @click="$emit('reject', selectedLeaveForDetail)">
              <feather-icon icon="XIcon" size="14" class="mr-1" />Reject
            </b-button>
          </div>
        </template>
      </div>
    </div>

    <!-- Footer: Close + Edit (pending) + Cancel (same canCancelLeave as table Actions) -->
    <template #modal-footer="{ hide }">
      <div class="leave-detail-footer-actions w-100">
        <b-button variant="outline-secondary" class="leave-detail-footer-btn leave-detail-footer-btn--close" @click="hide()">
          Close
        </b-button>
        <b-button
          v-if="showEditLeaveAction"
          variant="primary"
          class="leave-detail-footer-btn leave-detail-footer-btn--edit"
          @click="$emit('edit', selectedLeaveForDetail)"
        >
          <feather-icon icon="EditIcon" size="15" class="mr-50" />
          Edit Leave
        </b-button>
        <b-button
          v-if="showCancelLeaveAction"
          variant="outline-danger"
          class="leave-detail-footer-btn leave-detail-footer-btn--cancel"
          @click="$emit('cancel-leave', selectedLeaveForDetail)"
        >
          <feather-icon icon="MinusCircleIcon" size="15" class="mr-50" />
          Cancel Leave
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BCard, BBadge, BAlert, BFormGroup, BFormTextarea, BFormRadioGroup, BButton,
} from 'bootstrap-vue'

export default {
  name: 'LeaveDetailModal',
  components: {
    BModal, BCard, BBadge, BAlert, BFormGroup, BFormTextarea, BFormRadioGroup, BButton,
  },
  props: {
    selectedLeaveForDetail: { type: Object, default: null },
    leaveDetailComment: { type: String, default: '' },
    leaveDetailIsPaid: { type: Boolean, default: true },
    viewMode: { type: String, default: 'my' },
    canManageLeaves: { type: Boolean, default: false },
    selectedEmployeeLeaveBalance: { type: String, default: null },
    coBalance: { type: Number, default: null },
    currentUserDepartment: { type: String, default: '' },
    canApproveLeaveManagerStage: { type: Boolean, default: false },
    canApproveLeaveHRStage: { type: Boolean, default: false },
    canApproveLeaveAllStages: { type: Boolean, default: false },
    formatLeaveType: { type: Function, default: (t) => t },
    formatApprovalStatus: { type: Function, default: (s) => s },
    getApprovalStatusVariant: { type: Function, default: () => 'secondary' },
    formatDate: { type: Function, default: (d) => d },
    getUserName: { type: Function, default: () => '' },
    getApplicantDepartment: { type: Function, default: () => '' },
    parseCoRequestReason: { type: Function, default: (c) => c },
    needsLeaveApproval: { type: Function, default: () => false },
    canCancelLeave: { type: Function, default: () => false },
    isManagerOfEmployee: { type: Function, default: () => false },
    isLeavePendingManagerApproval: { type: Function, default: () => false },
    isLeavePendingHRApproval: { type: Function, default: () => false },
    isApplicantOnProbation: { type: Function, default: () => false },
  },
  computed: {
    durationLabel() {
      const leave = this.selectedLeaveForDetail
      if (!leave || leave.total_days == null || leave.total_days === '') return '-'
      const days = parseFloat(leave.total_days)
      if (Number.isNaN(days)) return '-'
      return days === 1 ? '1 day' : `${days} days`
    },
    coDaysRequestedLabel() {
      const leave = this.selectedLeaveForDetail
      if (!leave || leave.total_days == null || leave.total_days === '') return '-'
      const days = parseFloat(leave.total_days)
      if (Number.isNaN(days)) return '-'
      return days === 1 ? '1 day' : `${days} days`
    },
    coBalanceLabel() {
      if (this.coBalance == null) return '-'
      const days = parseFloat(this.coBalance)
      if (Number.isNaN(days)) return '-'
      return days === 1 ? '1 day' : `${days} days`
    },
    managerRemarks() {
      const leave = this.selectedLeaveForDetail
      if (!leave) return ''
      return leave.manager_remarks || leave.manager_comment || ''
    },
    /** Employee View: edit when leave is still pending approval */
    showEditLeaveAction() {
      if (!this.selectedLeaveForDetail || this.viewMode !== 'my') return false
      return typeof this.needsLeaveApproval === 'function'
        && this.needsLeaveApproval(this.selectedLeaveForDetail)
    },
    /** Same rule as Leaves table Actions Cancel */
    showCancelLeaveAction() {
      if (!this.selectedLeaveForDetail) return false
      return typeof this.canCancelLeave === 'function'
        && this.canCancelLeave(this.selectedLeaveForDetail)
    },
    /** Compact footer when only Close is shown (e.g. rejected leave) */
    leaveDetailFooterClass() {
      return (this.showEditLeaveAction || this.showCancelLeaveAction)
        ? 'leave-detail-footer leave-detail-footer--actions'
        : 'leave-detail-footer leave-detail-footer--compact'
    },
    /** Team/Org manager approval UI — independent of employee cancel footer */
    showTeamApprovalActions() {
      if (!this.selectedLeaveForDetail) return false
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return false
      if (!this.canManageLeaves) return false
      return typeof this.needsLeaveApproval === 'function'
        && this.needsLeaveApproval(this.selectedLeaveForDetail)
    },
  },
  methods: {
    show() {
      this.$refs['modal-leave-detail'].show()
    },
    hide() {
      this.$refs['modal-leave-detail'].hide()
    },
  },
}
</script>

<!-- Modals teleport to body; styles must be unscoped -->
<style src="./leave-detail-modal.css"></style>
